import { BreakpointObserver } from '@angular/cdk/layout';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { ProductsService } from 'idt-ecommerce-api/catalog/products/products.service';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { StoreLanguage } from 'idt-ecommerce-api/stores/stores.models';
import { CMSEvent, CMSSelectedHudsonEvent } from 'render/models/cms-event.model';
import { getStringParam } from 'render/render-shared/ui/render-product-prices/render-product-prices-utils';
import { RenderByDeviceService } from 'render/services/base-render-by-device/render-by-device.service';
import { CMSRenderEventsService } from 'render/services/cms-render-events.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { RenderGrowthbookConfigsService, GrowthbookConfig } from 'render/services/render-growthbook-configs/render-growthbook-configs.service';
import { Observable, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  takeUntil,
  shareReplay,
  catchError
} from 'rxjs/operators';
import { LazyDataInterface } from 'shared/lazy-load/lazy-load.interface';
import { Link } from 'shared/services/links.model';
import { SwiperContainer } from 'swiper/element';
import { SwiperOptions } from 'swiper/types/swiper-options';

import { HudsonFormatType, PieceHudsonSettings } from 'extensions/st/piece-hudson/front/models/piece-hudson.models';
import {
  ApiHudsonSearchContent
} from 'extensions/st/piece-hudson/front/services/api-hudson.models';
import { ApiHudsonService } from 'extensions/st/piece-hudson/front/services/api-hudson.service';
import { EmplifiHudsonTransformerService } from 'extensions/st/piece-hudson/front/services/emplifi-hudson-transformer.service';


export const DEFAULT_NUMBER_OF_SLIDES = 5;
export const FORMAT_DEFAULT = 'mosaic';

@Component({
  selector: 'piece-hudson-render',
  templateUrl: './piece-hudson-render.component.html',
  styleUrls: ['piece-hudson-render.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom
})
export class PieceHudsonRenderComponent
  extends BaseRenderComponent<PieceHudsonSettings> implements OnInit, AfterViewInit {

  @ViewChild('swiper') public swiperContainer?: ElementRef<SwiperContainer>;

  @HostBinding('style.--item-per-row')
  private get numberOfSlides(): number {
    const desktopSlides: number = this.settings.general.itemsPerRowDesktop
      ? Number(this.settings.general.itemsPerRowDesktop)
      : DEFAULT_NUMBER_OF_SLIDES;
    if (this.isMobile) {
      return this.settings.general.itemsPerRowMobile ? Number(this.settings.general.itemsPerRowMobile) : desktopSlides;
    }
    return desktopSlides;
  }

  @HostBinding('style.--mosaic-gap')
  private get gap(): string {
    if (this.isMobile) {
      return this.settings.general?.gridSpacingMobile ?? this.settings.general?.gridSpacingDesktop ?? '0px';
    }
    return this.settings.general.gridSpacingDesktop ?? '0px';
  }

  public get isMobile(): boolean {
    return this.renderByDeviceService.isMobile;
  }

  public get formatType(): HudsonFormatType {
    const formatTypeDesktop = this.settings.general.formatDesktop ?? FORMAT_DEFAULT;
    if (this.isMobile) {
      return this.settings.general.formatMobile ?? formatTypeDesktop;
    }
    return formatTypeDesktop;
  }

  public get redirectQueryParam(): '01' | '30' {
    return this.settings.general.redirectQueryParam ?? '01';
  }

  public swiperConfig: SwiperOptions = {};
  public products$!: Observable<ApiHudsonSearchContent[]>;
  public direction: 'ltr' | 'rtl' = 'ltr';

  constructor(
    protected readonly breakpointObserver: BreakpointObserver,
    protected readonly renderConfigService: RenderConfigurationService,
    protected readonly cd: ChangeDetectorRef,
    protected readonly el: ElementRef,
    private readonly contextService: ContextService,
    private readonly renderByDeviceService: RenderByDeviceService,
    private readonly apiHudsonService: ApiHudsonService,
    private readonly cmsRenderEventsService: CMSRenderEventsService,
    private readonly renderGrowthbookService: RenderGrowthbookConfigsService,
    private readonly emplifiHudsonTransformer: EmplifiHudsonTransformerService,
    private readonly productsService: ProductsService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.initConfig();
    const idAlbum = this.settings.general.idAlbum;
    const itemsToObtain = Number(this.settings.general.itemsToObtain ?? 10);
    const sort = this.settings.general.sort ?? 'ORDER';

    this.products$ = this.renderGrowthbookService.configurations$.pipe(
      startWith([] as GrowthbookConfig[]),
      switchMap((configs) => this.searchProducts(configs, idAlbum, itemsToObtain, sort)),
      switchMap((products) => this.enrichProductsWithLinks(products)),
      shareReplay({ bufferSize: 1, refCount: true }),
      takeUntil(this.clearSubscriptions$)
    );

    this.breakpointObserver.observe(
      this.renderConfigService.config.breakpoints.map((breakpoint) => breakpoint.bp)
    ).pipe(
      takeUntil(this.clearSubscriptions$)
    ).subscribe(() => {
      this.onMediaChange();
    });

    if (this.piece?.settings?.general) {
      this.piece.settings.general.displayProductSizesSelector = true;
    }
  }

  public ngAfterViewInit(): void {
    this.contextService.getCurrentLanguage$().pipe(
      distinctUntilChanged(),
      filter((currentLang: StoreLanguage) => !!currentLang?.direction),
      takeUntil(this.clearSubscriptions$)
    ).subscribe((currentLang: StoreLanguage) => {
      this.el.nativeElement.dir = currentLang.direction;
      this.direction = currentLang.direction;
      this.resetSwiper();
    });
  }

  public getProductImageData(product: ApiHudsonSearchContent): LazyDataInterface {
    const { url, width, height } = this.formatType !== 'custom'
      ? product.media.image_sizes.medium_square
      : product.media.image_sizes.original_converted;
    const productImage: LazyDataInterface = {
      src: url,
      height,
      width
    };
    return productImage;
  }

  public onProductLinkClick(product: ApiHudsonSearchContent): void {
    const cmsEvent: CMSEvent<CMSSelectedHudsonEvent> = {
      name: 'selectStradilook',
      type: 'click',
      data: {
        hudsonContent: product,
        name: 'select_content',
        contentType: 'home',
        actionType: 'ver_stradilook',
        link: product.link?.src,
        linkType: product?.media?.type?.toLowerCase() === 'video' ? 'video' : 'foto',
        eventData: 'catalogo'
      }
    };
    this.cmsRenderEventsService.emitEvent(cmsEvent);
  }

  public getProductDescription(product: ApiHudsonSearchContent): string {
    return product.products[0]?.product_groups[0]?.title;
  }

  // Changes in the variable used in the ngFor are not refreshed correctly when track by index.
  public trackByProduct(index: number, item: ApiHudsonSearchContent): string {
    return item.id;
  }

  public onFirstView(): void {
    const cmsEvent: CMSEvent<Record<string, string>> = {
      name: 'loadStradilook',
      type: 'load',
      data: {
        name: 'view_content',
        contentType: 'home',
        actionType: 'carrusel_stradilooks_visto',
        eventData: 'catalogo'
      },
      identifier: this.piece?.id
    };
    this.cmsRenderEventsService.emitEvent(cmsEvent);
  }

  private searchProducts(
    configs: GrowthbookConfig[],
    idAlbum: string,
    itemsToObtain: number,
    sort: string
  ): Observable<ApiHudsonSearchContent[]> {
    const isUsingEmplifi = getStringParam(configs, 'ugc-provider.name', 'dashsocial') === 'emplifi';
    const regionId = getStringParam(configs, 'ugc.region-id', '4648');

    if (isUsingEmplifi && this.settings.general.apiKey) {
      return this.apiHudsonService.searchEmplifi(
        idAlbum,
        this.settings.general.apiKey,
        itemsToObtain,
        sort,
        regionId
      ).pipe(
        map((response) => this.emplifiHudsonTransformer.transformEmplifiToHudsonFormat(response))
      );
    }

    return this.apiHudsonService.search(idAlbum, itemsToObtain, sort, this.country, this.lang).pipe(
      map(({ data }) => data)
    );
  }

  private enrichProductsWithLinks(
    products: ApiHudsonSearchContent[]
  ): Observable<(ApiHudsonSearchContent & { link: Link })[]> {
    if (products.length === 0) {
      return of([]);
    }

    const sourceIds = Array.from(
      new Set(
        products
          .map((p) => p.products[0]?.source_id)
          .filter((id): id is string => !!id)
      )
    );

    if (sourceIds.length === 0) {
      return of(products.map((p) => ({
        ...p,
        link: this.buildProductLink(p)
      })));
    }

    return this.productsService.productsDetailArray(
      sourceIds.join(','),
      undefined,
      'referenceIds'
    ).pipe(
      map((productDetails) => {
        const urlWebMap = new Map<string, string>();
        sourceIds.forEach((sourceId, index) => {
          const pd = productDetails[index];
          if (pd?.urlWeb) {
            urlWebMap.set(sourceId, pd.urlWeb);
          }
        });

        return products.map((product) => {
          const sourceId = product.products[0]?.source_id;
          const urlWeb = sourceId ? urlWebMap.get(sourceId) : undefined;
          return {
            ...product,
            link: this.buildProductLink(product, urlWeb)
          };
        });
      }),
      catchError(() => of(
        products.map((p) => ({
          ...p,
          link: this.buildProductLink(p)
        }))
      ))
    );
  }

  private buildProductLink(product: ApiHudsonSearchContent, urlWeb?: string): Link {
    const src = urlWeb ?? product.products[0]?.url;
    const stradiLooks = `${product.media.id}`;
    const srcWithQueryParam = src
      ? `${src}${src.includes('?') ? '&' : '?'}stradiLooks=${encodeURIComponent(stradiLooks)}&moreStradiLooks=true&xs=${this.redirectQueryParam}`
      : src;

    const link: Link = {
      type: 'externalProduct',
      src: srcWithQueryParam
    };
    return link;
  }

  private onMediaChange(): void {
    this.swiperConfig = {
      ...this.swiperConfig,
      slidesPerView: this.numberOfSlides,
      spaceBetween: Number.parseFloat(this.gap),
      slidesPerGroup: 1
    };
  }

  private initConfig(): void {
    this.swiperConfig = {
      loop: false,
      navigation: false,
      pagination: false,
      spaceBetween: Number.parseFloat(this.gap),
      slidesPerView: this.numberOfSlides,
      slidesPerGroup: 1,
      freeMode: true
    };
  }

  private resetSwiper(): void {
    this.swiperContainer?.nativeElement?.swiper.destroy();
    this.swiperContainer?.nativeElement?.swiper.init();
  }

}
